import { Box } from "@mui/joy";
import { useEffect, useState } from "react";
import { hatchlingChapters } from "../../data/hatchlingData";
import { Chapter } from "../../types/novellaTypes";
import {
  getReadPoems,
  hatchlingChapterToPoemMapping,
  isChapterCompleted,
  markChapterAsCompleted,
  markPoemAsRead,
} from "../../utils/cookieUtils";
import HatchlingChapterNavigation from "./HatchlingChapterNavigation";
import HatchlingCharacterGallery from "./HatchlingCharacterGallery";
import HatchlingReaderContent from "./HatchlingReaderContent";
import HatchlingReaderHeader from "./HatchlingReaderHeader";

const HatchlingReader = () => {
  const [currentChapter, setCurrentChapter] = useState<Chapter>(
    hatchlingChapters[0]
  );

  // On mount, sync poems with completed chapters
  // This ensures poems are unlocked if chapters were completed in previous sessions
  useEffect(() => {
    const readPoems = getReadPoems();
    Object.entries(hatchlingChapterToPoemMapping).forEach(
      ([chapterId, poemId]) => {
        if (isChapterCompleted(chapterId) && !readPoems.includes(poemId)) {
          markPoemAsRead(poemId);
        }
      }
    );
  }, []);

  // Check URL params for chapter selection
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const chapterId = params.get("chapter");
    if (chapterId) {
      const chapter = hatchlingChapters.find((c) => c.id === chapterId);
      if (chapter) {
        setCurrentChapter(chapter);
      }
    }
  }, []);

  const handleChapterChange = (chapter: Chapter) => {
    setCurrentChapter(chapter);
    // Mark current chapter as completed before moving to new chapter
    markChapterAsCompleted(currentChapter.id);
    // Scroll to top when changing chapter
    window.scrollTo(0, 0);
    // Update URL without page reload
    const url = new URL(window.location.href);
    url.searchParams.set("chapter", chapter.id);
    window.history.pushState({}, "", url.toString());
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "background.body",
        color: "text.primary",
        transition: "background-color 0.3s, color 0.3s",
        px: { xs: 2, md: 4 },
        pb: 4,
      }}
    >
      <HatchlingReaderHeader />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 4,
          width: "100%",
          maxWidth: "1400px",
          mx: "auto",
          flex: 1,
        }}
      >
        <Box sx={{ width: { xs: "100%", md: "25%" }, mb: { xs: 2, md: 0 } }}>
          <HatchlingChapterNavigation
            chapters={hatchlingChapters}
            currentChapter={currentChapter}
            onChapterChange={handleChapterChange}
          />
        </Box>

        <Box sx={{ width: { xs: "100%", md: "75%" } }}>
          <HatchlingReaderContent
            chapter={currentChapter}
            onComplete={() => markChapterAsCompleted(currentChapter.id)}
          />
          <HatchlingCharacterGallery />
        </Box>
      </Box>
    </Box>
  );
};

export default HatchlingReader;
